import React, { useRef, useState } from 'react';

import { Alert } from '@components/Alert';
import { Layout } from '@components/Layout';
import { CargoTrackingContainer } from '@components/utilities/CargoTracking/CargoTrackingContainer';
import { CargoTrackingHeader } from '@components/utilities/CargoTracking/CargoTrackingHeader';
import { TextInput } from '@components/utilities/CargoTracking/Form';
import Grid from '@material-ui/core/Grid';
import { CargoTrackingResultQuery } from '@pages/utilities/CargoTracking/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createValidate } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { sanitizeJapaneseNumber } from '@utils/sanitize';
import { iota, stringifySearch } from '@utils/types';
import { Form, Formik, FormikProps } from 'formik';
import * as t from 'io-ts';
import { useHistory } from 'react-router-dom';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '貨物追跡',
    prompt: '貨物追跡',
    track: '追跡',
    nThItem: (n: number) => `${n}`,
    hint: `使い方

・D2D番号(Y番号)を入力してください。
・D2D番号(Y番号)入力後「追跡」ボタンをクリックしてください。
・最大10件まで一度に検索することができます。`,
    errorAllEmpty: '全部で空欄の状態ではご提出いただけません。内容のご確認をお願いいたします。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  hint: {
    ...common.textGray800,
    ...common.normalText,
    whiteSpace: 'pre-wrap',
    marginBottom: 100,
  },
  divider: {
    height: 1,
    backgroundColor: theme.palette.grey['300'],
    margin: '50px 0',
  },
}));

const schema = h.type({
  cargo1: h.string(),
  cargo2: h.string(),
  cargo3: h.string(),
  cargo4: h.string(),
  cargo5: h.string(),
  cargo6: h.string(),
  cargo7: h.string(),
  cargo8: h.string(),
  cargo9: h.string(),
  cargo10: h.string(),
});

const initialValues: t.TypeOf<typeof schema> = import.meta.env.DEV
  ? {
      cargo1: '20210012',
      cargo2: '20230012',
      cargo3: '２０２００００７',
      cargo4: '20212345',
      cargo5: '20211451',
      cargo6: 'newcompany',
      cargo7: '',
      cargo8: '',
      cargo9: '',
      cargo10: '',
    }
  : {
      cargo1: '',
      cargo2: '',
      cargo3: '',
      cargo4: '',
      cargo5: '',
      cargo6: '',
      cargo7: '',
      cargo8: '',
      cargo9: '',
      cargo10: '',
    };

export const CargoTracking: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();

  const formikRef = useRef<FormikProps<t.TypeOf<typeof schema>> | null>(null);

  const [error, setError] = useState('');

  const renderCargoInput = (n: number) => {
    return (
      <TextInput key={`cargo${n}`} name={`cargo${n}`} label={strings.nThItem(n)} prefix="Y-" />
    );
  };

  return (
    <Layout
      title={strings.title}
      breadcrumbs={['/utilities/Dashboard', '/utilities/CargoTracking/CargoTracking']}
    >
      <CargoTrackingContainer>
        <Alert open={Boolean(error)} severity="error" onClose={() => setError('')}>
          {error}
        </Alert>

        <div style={{ height: 16 }} />

        <CargoTrackingHeader
          title={strings.prompt}
          buttonTitle={strings.track}
          onClickButton={() => formikRef.current!.submitForm()}
        />

        <Formik
          innerRef={formikRef}
          validate={createValidate(schema)}
          initialValues={initialValues}
          onSubmit={async (values) => {
            if (Object.values(values).every((v) => v.trim() === '')) {
              setError(strings.errorAllEmpty);
              return;
            }

            const trackNos = Object.values(values)
              .map(sanitizeJapaneseNumber)
              .filter((v) => v !== '')
              .map((v) => `Y-${v}`);

            const searchString = stringifySearch<CargoTrackingResultQuery>({ trackNos });
            history.push(`${url('/utilities/CargoTracking/CargoTrackingResult')}?${searchString}`);
          }}
        >
          <Form>
            <Grid container spacing={6}>
              <Grid item xs={12} sm={6} className={classes.verticalSpace1}>
                {iota(5, 1).map((i) => renderCargoInput(i))}
              </Grid>
              <Grid item xs={12} sm={6} className={classes.verticalSpace1}>
                {iota(5, 6).map((i) => renderCargoInput(i))}
              </Grid>
            </Grid>
          </Form>
        </Formik>
        <div className={classes.divider} />
        <div className={classes.hint}>{strings.hint}</div>
      </CargoTrackingContainer>
    </Layout>
  );
};
